import { useQuery } from "@apollo/client";
import { Box, MenuItem } from "@mui/material";
import { useFormikContext } from "formik";
import { ReactElement } from "react";

import {
  GetNgpVanSurveyQuestionWithResponsesQuery,
  GetNgpVanSurveyQuestionWithResponsesQueryVariables,
  NgpVanDatabaseModeGqlType,
  NgpVanIntegration,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { GET_NGPVAN_SURVEY_QUESTION_WITH_RESPONSES } from "~frontend/graphql/integration";

import Select, { FormikSelect } from "../FormSelect";
import { FormikNgpVanActivistCodeAutocomplete } from "./NgpVanActivistCodeAutocomplete";
import { FormikNgpVanSurveyQuestionAutocomplete } from "./NgpVanSurveyQuestionAutocomplete";

export type NgpVanTagActionType =
  | "APPLY_ACTIVIST_CODE"
  | "APPLY_RESULT_CODE"
  | "APPLY_SURVEY_RESPONSE";

function ActionObjectPicker({
  integrationId,
  databaseMode,
  integration,
  namePrefix,
  actionType,
}: {
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  integration: NgpVanIntegration;
  namePrefix: string;
  actionType: NgpVanTagActionType | "";
}): ReactElement {
  switch (actionType) {
    case "":
      return (
        <Select label="" disabled fullWidth value="">
          <MenuItem value=""></MenuItem>
        </Select>
      );
    case "APPLY_RESULT_CODE":
      return (
        <FormikSelect
          label="Canvass Result"
          name={`${namePrefix}resultCodeId`}
          fullWidth
          sx={{ mb: 0 }}
        >
          {integration.metadata[
            databaseMode === NgpVanDatabaseModeGqlType.MyVoters
              ? "vanMyVotersResultCodes"
              : "vanMyCampaignResultCodes"
          ].map(resultCode => (
            <MenuItem
              key={resultCode.resultCodeId}
              value={resultCode.resultCodeId}
            >
              {resultCode.name}
            </MenuItem>
          ))}
        </FormikSelect>
      );
    case "APPLY_ACTIVIST_CODE":
      return (
        <FormikNgpVanActivistCodeAutocomplete
          databaseMode={databaseMode}
          integrationId={integrationId}
          label="Activist Code"
          name={`${namePrefix}activistCodeId`}
        />
      );
    case "APPLY_SURVEY_RESPONSE":
      return (
        <FormikNgpVanSurveyQuestionAutocomplete
          databaseMode={databaseMode}
          integrationId={integrationId}
          label="Survey Question"
          name={`${namePrefix}surveyQuestionId`}
          responseFieldName={`${namePrefix}surveyResponseId`}
        />
      );
  }
}

function SurveyResponsePicker({
  integrationId,
  databaseMode,
  surveyQuestionId,
  name,
}: {
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  surveyQuestionId: number;
  name: string;
}): ReactElement {
  const { data, loading, error } = useQuery<
    GetNgpVanSurveyQuestionWithResponsesQuery,
    GetNgpVanSurveyQuestionWithResponsesQueryVariables
  >(GET_NGPVAN_SURVEY_QUESTION_WITH_RESPONSES, {
    variables: {
      input: {
        integrationId,
        databaseMode,
        surveyQuestionId,
      },
    },
  });

  const formik = useFormikContext<any>();
  const selectedValue = formik.getFieldProps(name).value;

  return (
    <FormikSelect label="Survey Response" name={name} fullWidth>
      {loading && (
        <MenuItem value={selectedValue} disabled>
          Loading...
        </MenuItem>
      )}
      {error && (
        <MenuItem value={selectedValue} disabled>
          Error loading survey responses
        </MenuItem>
      )}
      {data?.getNgpVanSurveyQuestion?.responses.map(response => (
        <MenuItem
          key={response.surveyResponseId}
          value={response.surveyResponseId}
        >
          {response.name}
        </MenuItem>
      )) ?? null}
    </FormikSelect>
  );
}

export default function FormikNgpVanActionField({
  databaseMode,
  integrationId,
  integration,
  namePrefix,
}: {
  databaseMode: NgpVanDatabaseModeGqlType;
  integrationId: string;
  integration: NgpVanIntegration;
  namePrefix: string;
}): ReactElement {
  const formik = useFormikContext<any>();

  const actionType: NgpVanTagActionType | "" = formik.getFieldProps(
    `${namePrefix}actionType`
  ).value;
  const surveyQuestionId = formik.getFieldProps(
    `${namePrefix}surveyQuestionId`
  ).value;

  return (
    <Box>
      <FlexBox>
        <Box sx={{ width: "200px" }}>
          <FormikSelect
            label="Action Type"
            name={`${namePrefix}actionType`}
            fullWidth
            overrideFormikProps={field => ({
              ...field,
              onChange: (event: React.ChangeEvent<any>) => {
                // instead of just setting the single value, we reset everything
                // other than the action type
                Promise.all([
                  formik.setFieldValue(
                    `${namePrefix}actionType`,
                    event.target.value
                  ),
                  formik.setFieldValue(`${namePrefix}resultCodeId`, ""),
                  formik.setFieldValue(
                    `${namePrefix}activistCodeId`,
                    undefined
                  ),
                  formik.setFieldValue(
                    `${namePrefix}surveyQuestionId`,
                    undefined
                  ),
                  formik.setFieldValue(`${namePrefix}surveyResponseId`, ""),

                  formik.setFieldTouched(`${namePrefix}actionType`, true),
                  formik.setFieldTouched(`${namePrefix}resultCodeId`, false),
                  formik.setFieldTouched(`${namePrefix}activistCodeId`, false),
                  formik.setFieldTouched(
                    `${namePrefix}surveyQuestionId`,
                    false
                  ),
                  formik.setFieldTouched(
                    `${namePrefix}surveyResponseId`,
                    false
                  ),
                ]).catch(console.error);
              },
            })}
          >
            <MenuItem value="APPLY_RESULT_CODE">Canvass Result</MenuItem>
            <MenuItem value="APPLY_ACTIVIST_CODE">Activist Code</MenuItem>
            <MenuItem value="APPLY_SURVEY_RESPONSE">Survey Response</MenuItem>
          </FormikSelect>
        </Box>
        <Box sx={{ flex: 1, ml: "-1px" }}>
          <ActionObjectPicker
            integrationId={integrationId}
            databaseMode={databaseMode}
            integration={integration}
            namePrefix={namePrefix}
            actionType={actionType}
          />
        </Box>
      </FlexBox>
      {actionType == "APPLY_SURVEY_RESPONSE" && !!surveyQuestionId && (
        <SurveyResponsePicker
          integrationId={integrationId}
          databaseMode={databaseMode}
          name={`${namePrefix}surveyResponseId`}
          surveyQuestionId={surveyQuestionId}
        />
      )}
    </Box>
  );
}
